import {
  CalendarIcon,
  CategoryIcon,
  CompanyIcon,
  GlobeIcon,
  HistoryIcon,
  LabelIcon,
  MusicIcon,
  ReleaseIcon,
  ScheduleIcon,
  SellIcon,
  TrendingUpIcon,
  WaveformIcon,
} from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
// Per-surface facet UI declarations. Keys must match the Solr-side registries
// in @garimpa/search (WORK_RELEASE_REGISTRY / RELEASE_GROUP_REGISTRY) — those
// own key→field; this owns key→presentation. The shared <FacetFilterBar> +
// useFacetFilters render any surface from one of these configs.

export type FacetWidget = "multiselect" | "single" | "range";

export interface FacetDef {
  /** Facet key — must exist in the surface's Solr registry. */
  key: string;
  /** i18n key under the "facets" namespace. */
  labelKey: string;
  widget: FacetWidget;
  match?: "any" | "all";
  /** Semantic icon component. */
  icon: AppIcon;
}

export interface SortDef {
  value: string;
  /** i18n key under the "facets" namespace. */
  labelKey: string;
  /** Semantic icon component. */
  icon: AppIcon;
}

export interface SurfaceConfig {
  facets: FacetDef[];
  sorts: SortDef[];
  defaultSort: string;
}

/** Work detail page — facets over the releases of a single work. */
export const WORK_SURFACE: SurfaceConfig = {
  facets: [
    { key: "format", labelKey: "format", widget: "single", icon: ReleaseIcon },
    { key: "country", labelKey: "country", widget: "single", icon: GlobeIcon },
    { key: "year", labelKey: "year", widget: "range", icon: CalendarIcon },
    {
      key: "formatSet",
      labelKey: "formatSet",
      widget: "multiselect",
      match: "all",
      icon: ReleaseIcon,
    },
    { key: "label", labelKey: "label", widget: "multiselect", match: "all", icon: SellIcon },
    { key: "company", labelKey: "company", widget: "multiselect", match: "all", icon: CompanyIcon },
    { key: "tag", labelKey: "tag", widget: "multiselect", match: "all", icon: LabelIcon },
  ],
  sorts: [
    { value: "date-desc", labelKey: "newestFirst", icon: ScheduleIcon },
    { value: "date-asc", labelKey: "oldestFirst", icon: HistoryIcon },
  ],
  defaultSort: "date-desc",
};

/** Grouped release search — artist & company discography (and search music tab). */
export const RELEASE_GROUP_SURFACE: SurfaceConfig = {
  facets: [
    { key: "format", labelKey: "format", widget: "single", icon: ReleaseIcon },
    { key: "type", labelKey: "type", widget: "single", icon: CategoryIcon },
    { key: "genre", labelKey: "genre", widget: "multiselect", icon: MusicIcon },
    { key: "style", labelKey: "style", widget: "multiselect", icon: WaveformIcon },
    { key: "country", labelKey: "country", widget: "single", icon: GlobeIcon },
    { key: "year", labelKey: "year", widget: "range", icon: CalendarIcon },
    { key: "label", labelKey: "label", widget: "multiselect", match: "all", icon: SellIcon },
    { key: "company", labelKey: "company", widget: "multiselect", match: "all", icon: CompanyIcon },
  ],
  sorts: [
    { value: "popular", labelKey: "popular", icon: TrendingUpIcon },
    { value: "newest", labelKey: "newestFirst", icon: ScheduleIcon },
    { value: "oldest", labelKey: "oldestFirst", icon: HistoryIcon },
  ],
  defaultSort: "popular",
};

const DISCOGRAPHY_RELEASE_GROUP_FACETS: FacetDef[] = [
  { key: "format", labelKey: "format", widget: "single", icon: ReleaseIcon },
  { key: "genre", labelKey: "genre", widget: "multiselect", icon: MusicIcon },
  { key: "style", labelKey: "style", widget: "multiselect", icon: WaveformIcon },
  { key: "country", labelKey: "country", widget: "single", icon: GlobeIcon },
  { key: "year", labelKey: "year", widget: "range", icon: CalendarIcon },
  { key: "label", labelKey: "label", widget: "multiselect", match: "all", icon: SellIcon },
  { key: "company", labelKey: "company", widget: "multiselect", match: "all", icon: CompanyIcon },
];

const ARTIST_DISCOGRAPHY_RELEASE_GROUP_FACETS: FacetDef[] = DISCOGRAPHY_RELEASE_GROUP_FACETS.filter(
  (facet) => facet.key !== "genre" && facet.key !== "style",
);

const DISCOGRAPHY_RELEASE_GROUP_SORTS: SortDef[] = [
  { value: "popular", labelKey: "popular", icon: TrendingUpIcon },
  { value: "newest", labelKey: "newestFirst", icon: ScheduleIcon },
  { value: "oldest", labelKey: "oldestFirst", icon: HistoryIcon },
];

export const ARTIST_DISCOGRAPHY_SURFACE: SurfaceConfig = {
  facets: ARTIST_DISCOGRAPHY_RELEASE_GROUP_FACETS,
  sorts: DISCOGRAPHY_RELEASE_GROUP_SORTS,
  defaultSort: "oldest",
};

export const COMPANY_DISCOGRAPHY_SURFACE: SurfaceConfig = {
  facets: DISCOGRAPHY_RELEASE_GROUP_FACETS,
  sorts: DISCOGRAPHY_RELEASE_GROUP_SORTS,
  defaultSort: "popular",
};

/**
 * Public offers — release taxonomy and the work's original year; offer-specific
 * controls stay on the route. Year options are decades, not per-year counts.
 */
export const OFFERS_SURFACE: SurfaceConfig = {
  facets: [
    { key: "genre", labelKey: "genre", widget: "multiselect", icon: MusicIcon },
    { key: "style", labelKey: "style", widget: "multiselect", icon: WaveformIcon },
    { key: "year", labelKey: "year", widget: "range", icon: CalendarIcon },
  ],
  sorts: [],
  defaultSort: "price_drop",
};
